/**
 * THE BASKET A HAND-OFF CARRIED, PUT INTO THIS BROWSER'S BASKET (moved out of Pages/Checkout on 2026-10-25, so the
 * checkout and the component that serves every other store page seed it the same way).
 *
 * MERGED, NOT REPLACED: "if there's another session there in the browser, add them together" (the owner,
 * 2026-10-06). The same variant in both baskets is one line at the SUM; the cart's own shelf ceiling still holds it.
 * ONCE PER HAND-OFF, ON THE DEVICE: the same address opened twice (a reload, a second tap, a new tab) must not add
 * twice, so the hand-off is remembered for HANDOFF_SEEN_MS under handoffSeenKey.
 */
import { fetchPublicProductWindow } from '../api/storeApi';
import { brandName } from './productBrands';
import { variantPurchaseCap } from './stockCap';
import { HANDOFF_SEEN_MS, handoffSeenKey } from './openInBrowser';

/**
 * A cart line's snapshot from the product window's card (public-product.serializer.ts), as the product page writes
 * one at add time — for the basket a handoff carried in the address.
 */
export function handoffSnapshot(product, variant, language) {
  const al = product.axisLabels ?? {};
  const options = [
    variant.color && { key: 'color', label: al.color || 'Color', value: variant.color },
    variant.size && { key: 'size', label: al.size || 'Size', value: variant.size },
    variant.style && { key: 'style', label: al.style || 'Type', value: variant.style },
  ].filter(Boolean);
  const firstImage = (list) => {
    const img = Array.isArray(list) ? list[0] : null;
    return typeof img === 'string' ? img : (img && typeof img.url === 'string' ? img.url : null);
  };
  return {
    name: product.name + (variant.name ? ` — ${variant.name}` : ''),
    image: firstImage(variant.images) ?? firstImage(product.images) ?? null,
    price: Number(variant.price ?? product.priceFrom ?? 0),
    maxQty: variantPurchaseCap(product, variant),
    brand: brandName(product.brand, language) || null,
    options,
    variantAttrs: { color: variant.color ?? null, size: variant.size ?? null, style: variant.style ?? null },
  };
}

/**
 * Reads the products the lines name and adds each line that still exists. Returns, per line added, what the basket
 * held and what was added — ids and counts, nothing personal. `cart` is `{ items, addItem }` (the useCart shape).
 */
export async function addHandoffLines({ salonSlug, wanted, cart, language, isCancelled = () => false }) {
  const products = await fetchPublicProductWindow(salonSlug, [...new Set(wanted.map((w) => w.productId))]);
  if (isCancelled()) return [];
  const merged = [];
  for (const line of wanted) {
    const product = (Array.isArray(products) ? products : []).find((p) => p && p.id === line.productId);
    const variant = product ? (product.variants || []).find((v) => v && v.id === line.variantId) : null;
    if (!product || !variant) continue;
    const held = (cart.items || []).find((i) => i.variantId === line.variantId);
    cart.addItem({
      productId: product.id,
      variantId: variant.id,
      quantity: line.quantity,
      snapshot: handoffSnapshot(product, variant, language),
    });
    merged.push({ variantId: variant.id, held: held ? held.quantity : 0, added: line.quantity });
  }
  return merged;
}

/**
 * Seeds the lines an `items=` named, once per hand-off. Returns the number of lines added (0 when this device has
 * already seeded the same hand-off within HANDOFF_SEEN_MS). `outcome`, when given, is filled with `{ seen }` or
 * `{ merged }` for a caller that tells what happened (the checkout's `checkout_handoff_restored` note).
 */
export async function seedHandoffBasket({
  salonSlug, items, cart, storage = typeof localStorage !== 'undefined' ? localStorage : null, now = Date.now(),
  language, isCancelled, outcome,
}) {
  const wanted = Array.isArray(items) ? items : [];
  if (!salonSlug || !wanted.length) return 0;
  const key = handoffSeenKey(salonSlug, wanted);
  try {
    const at = Number(storage && storage.getItem(key));
    if (Number.isFinite(at) && at > 0 && now - at < HANDOFF_SEEN_MS) {
      if (outcome) outcome.seen = true;
      return 0;
    }
  } catch (_) { /* unreadable storage: seed, the cart's cap holds */ }
  const merged = await addHandoffLines({ salonSlug, wanted, cart, language, isCancelled });
  try { if (storage) storage.setItem(key, String(now)); } catch (_) { /* may seed twice; the cart's cap holds */ }
  if (outcome) outcome.merged = merged;
  return merged.length;
}
